/**
 * EndpointsAccessPanel — veřejné body Codable + jednoduchý přehled oprávnění.
 *
 * Adresy hubu jsou trvalé identity (published_endpoints): existují i před
 * publishem, on/off je jen stav. Přehled per projekt: endpoint, členové
 * read/write (entity_group codable-project:<slug>), počet tokenů.
 *
 * i18n: codableAccess.* (cs/en/it/cvm) — žádné hardcoded UI stringy.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api';
import { Badge, Card, CardContent, SectionHeader, StatusDot } from '../../../components/ui';
import { CodableAccessCard } from '../../../components/CodableAccessCard';

interface HubPoint {
  domain: string;
  published: boolean;
  url: string | null;
}
interface ProjectAccess {
  slug: string;
  name: string | null;
  tenant_id: string | null;
  endpoint: { domain: string; published: boolean; url: string | null } | null;
  members: { write: string[]; read: string[] };
  tokens: number;
}
interface Overview {
  ok: boolean;
  hub: { ui: HubPoint; mcp: HubPoint };
  total_projects: number;
  projects: ProjectAccess[];
}


export function EndpointsAccessPanel() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['codable-access-overview'],
    queryFn: () => api.mcp<Overview>('codable.access_overview', {}),
    staleTime: 30_000,
  });

  if (q.isLoading) {
    return (
      <div className='text-sm text-gray-500 p-4'>
        {t('codableAccess.loading', 'Loading access overview…')}
      </div>
    );
  }
  if (q.isError || !q.data?.ok) {
    return (
      <div className='text-sm text-red-400 p-4'>
        {t('codableAccess.loadFailed', 'Could not load access overview')}
        {q.error ? `: ${(q.error as Error).message}` : ''}
      </div>
    );
  }
  const d = q.data;

  return (
    <div className='space-y-4'>
      {/* Hub UI/MCP: TÁŽ karta jako v sekci Veřejný přístup — přepínače
          (ne tlačítka) a u UI navíc SSO. Jedna komponenta, aby se obě místa
          nerozešla (Filip 2026-08-17). */}
      <CodableAccessCard />

      <Card>
        <CardContent>
          <SectionHeader
            title={t('codableAccess.projectsTitle', 'Project permissions ({{n}})', {
              n: d.total_projects,
            })}
          />
          {d.projects.length === 0 && (
            <div className='text-sm text-gray-500 py-2'>
              {t('codableAccess.noProjects', 'No projects.')}
            </div>
          )}
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='text-left text-[11px] text-gray-500 uppercase'>
                  <th className='py-1 pr-3'>{t('codableAccess.colProject', 'Project')}</th>
                  <th className='py-1 pr-3'>{t('codableAccess.colEndpoint', 'Endpoint')}</th>
                  <th className='py-1 pr-3'>{t('codableAccess.colWrite', 'Write')}</th>
                  <th className='py-1 pr-3'>{t('codableAccess.colRead', 'Read')}</th>
                  <th className='py-1'>{t('codableAccess.colTokens', 'Tokens')}</th>
                </tr>
              </thead>
              <tbody>
                {d.projects.map((p) => (
                  <tr key={p.slug} className='border-t border-gray-800 align-top'>
                    <td className='py-2 pr-3'>
                      <div className='font-semibold text-white'>{p.slug}</div>
                      {p.name && <div className='text-[11px] text-gray-500'>{p.name}</div>}
                    </td>
                    <td className='py-2 pr-3'>
                      {p.endpoint
                        ? (
                          <div className='flex items-center gap-2'>
                            <StatusDot
                              status={p.endpoint.published ? 'running' : 'stopped'}
                            />
                            <span className='text-[11px] font-mono text-gray-400 break-all'>
                              {p.endpoint.domain}
                            </span>
                          </div>
                        )
                        : (
                          <span className='text-[11px] text-gray-600'>
                            {t('codableAccess.dash', '—')}
                          </span>
                        )}
                    </td>
                    <td className='py-2 pr-3'>
                      {p.members.write.length
                        ? p.members.write.map((m) => (
                          <Badge key={m} variant='success'>{m}</Badge>
                        ))
                        : (
                          <span className='text-[11px] text-gray-600'>
                            {t('codableAccess.nobody', 'nobody')}
                          </span>
                        )}
                    </td>
                    <td className='py-2 pr-3'>
                      {p.members.read.length
                        ? p.members.read.map((m) => <Badge key={m}>{m}</Badge>)
                        : (
                          <span className='text-[11px] text-gray-600'>
                            {t('codableAccess.nobody', 'nobody')}
                          </span>
                        )}
                    </td>
                    <td className='py-2 text-gray-300'>{p.tokens}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
